import { ColumnType } from 'antd/lib/table';
import moment from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import BackOfficeTable, {
  Props as BackOfficeTableProps
} from 'components/tables/BackOfficeTable';
import { INTERNAL_ADMIN_ROLE_TO_LABEL, InternalAdminRole } from 'constants/internal-admin-roles';
import { SEARCH_PARAMS } from 'constants/search-params';
import { DATE_TIME_FORMAT } from 'constants/time';
import useTranslation from 'hooks/useTranslation';
import {
  selectBackOfficeUsersAsArray,
  selectBackOfficeUsersTotal
} from 'store/modules/backOfficeUsers/selectors';
import User from 'types/models/user';

const renderAdminRole = (value: User['adminRole']): string => INTERNAL_ADMIN_ROLE_TO_LABEL[value];
const renderBooleanNotifications = (value: boolean) =>
  value ? 'Enabled' : 'Disabled';

const adminRoleSorter = (a: User, b: User): number => {
  // Place customers/normal users last in both ascending and descending order
  if (a.adminRole === InternalAdminRole._NONE && b.adminRole !== InternalAdminRole._NONE) return 1;
  if (a.adminRole !== InternalAdminRole._NONE && b.adminRole === InternalAdminRole._NONE) return -1;
  // Otherwise, sort alphabetically
  if (a.adminRole < b.adminRole) return -1;
  if (a.adminRole > b.adminRole) return 1;
  return 0;
};

const loadAction = 'LOAD_BACK_OFFICE_USERS';

const getColumns = (): ColumnType<User>[] => [
  {
    title: 'First Name',
    dataIndex: 'firstName',
    width: 200,
    ellipsis: true,
    fixed: 'left',
    sorter: true
  },
  {
    title: 'Last Name',
    dataIndex: 'lastName',
    width: 200,
    ellipsis: true,
    sorter: true
  },
  {
    title: 'ID',
    dataIndex: 'id',
    width: 75,
    ellipsis: true,
    sorter: true
  },
  {
    title: 'Username',
    dataIndex: 'username',
    width: 200,
    ellipsis: true,
    sorter: true
  },
  {
    title: 'Email',
    dataIndex: 'email',
    width: 200,
    ellipsis: true,
    sorter: true
  },
  {
    title: 'Mobile Number',
    dataIndex: 'mobileNumberFormatted',
    width: 200,
    ellipsis: true
  },
  {
    title: 'Landline Number',
    dataIndex: 'landlineNumberFormatted',
    width: 200,
    ellipsis: true
  },
  {
    title: 'Created At',
    dataIndex: 'createdAt',
    width: 250,
    ellipsis: false,
    sorter: true,
    render: (createdAt: number) => (
      <div>{moment(createdAt).format(DATE_TIME_FORMAT.DEFAULT)}</div>
    )
  },
  {
    title: 'Internal Admin Role',
    dataIndex: 'adminRole',
    width: 125,
    ellipsis: false,
    sorter: adminRoleSorter,
    render: renderAdminRole
  },
  {
    title: 'SMS Notifications',
    dataIndex: 'enableSmsNotifications',
    width: 125,
    ellipsis: false,
    render: renderBooleanNotifications
  },
  {
    title: 'Email Notifications',
    dataIndex: 'enableEmailNotifications',
    width: 125,
    ellipsis: false,
    render: renderBooleanNotifications
  }
];

function BackOfficeUsersTable({
  onPaginationChange
}: Pick<BackOfficeTableProps<User>, 'onPaginationChange'>): JSX.Element {
  const data: User[] = useSelector(selectBackOfficeUsersAsArray, shallowEqual);
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = Number(searchParams.get(SEARCH_PARAMS.BACK_OFFICE.SELECTED_USER)) || undefined;

  const backOfficeUsersTotal = useSelector(
    selectBackOfficeUsersTotal,
    shallowEqual
  );

  const onRowClick = useCallback((id: number) => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.DRAWER.key);
      newSearchParams.set(SEARCH_PARAMS.BACK_OFFICE.SELECTED_USER, String(id));
      return newSearchParams;
    });
  }, [setSearchParams]);

  const { t } = useTranslation();
  const localisedColumns = useMemo(
    () => {
      return getColumns().map((column) => {
        if (column.dataIndex === 'mobileNumberFormatted') {
          column.title = t('MOBILE_NUMBER');
        } else if (column.dataIndex === 'enableSmsNotifications') {
          column.title = `${t('SMS')} Notifications`;
        }
        return column;
      });
    },
    [t]
  );

  return (
    <BackOfficeTable<User>
      columns={localisedColumns}
      data={data}
      total={backOfficeUsersTotal}
      loadActionType={loadAction}
      onRowClick={onRowClick}
      onPaginationChange={onPaginationChange}
      emptyText="No users found"
      selectedRowId={userId}
    />
  );
}

export default BackOfficeUsersTable;
